// Visual tokens for the District Insights dashboard: the palette, the surfaces
// everything sits on, and the semantic color maps that encode a status, a dosage
// tier, a mastery band, a robot state or a subject.
//
// No React, no imports — the base layer every other impact/ module builds on.
// Anything that computes rather than names a value lives in model.js; anything
// that renders lives in ui.js or primitives.js.

// ---- palette (validated via dataviz skill) ---------------------------------
const STATUS = {
  good: { label: "Thriving", color: "#0ca30c" },
  warning: { label: "Reaching few", color: "#e0920a" },
  critical: { label: "Needs support", color: "#d03b3b" },
};
// categorical hues for highlighted schools (fixed order, never cycled)
const SERIES_HUES = ["#2a78d6", "#1baf7a", "#4a3aa7"];
const INK = "#2b2a28";
const MUTED = "#898781";
const GRID = "#e1e0d9";
const CONTEXT = "#c9c8bf"; // faint gray for un-highlighted schools

// ---- surface + chrome tokens -----------------------------------------------
// One source of truth for the boxes everything sits in. Spelled out per view,
// card borders and row dividers drift into three near-identical grays doing one
// job (`rgba(11,11,11,0.10)`, `rgba(11,11,11,0.06)`, `GRID`). GRID wins
// everywhere.
const SURFACE = "#fff";
const CARD_RADIUS = 12;
const CARD_BORDER = GRID;
const ROW_DIVIDER = GRID;
// Expanded-panel fill, indexed by nesting depth (1-based; deeper levels reuse
// the last entry). Recessed relative to SURFACE so a drill-down reads as
// "inside" the row above it.
const NEST_BG = ["#faf9f6", "#f3f2ec"];
const nestBg = (depth) => NEST_BG[Math.min(depth, NEST_BG.length) - 1];
// Tray behind a segmented control; the active pill is SURFACE on top of it.
const TRAY = "#f4f3ef";
// Body prose — darker than MUTED (which is for labels/secondary numbers) so
// paragraphs stay comfortably readable at 3:1+ against the frosted panel.
const PROSE = "#52514e";

// Breakdown dimensions. A "segment" is one row/dot/line; the only thing that
// changes across dimensions is what we fan out over (server-side). The noun map
// lets the shared tabs relabel themselves for whichever dimension is in force.
// `district` is the org rollup and stays constant regardless of dimension.
const SEG_NOUN = {
  school: { one: "school", many: "schools", One: "School", Many: "Schools" },
  // teacher dimension segments by class owner (a teacher where one is linked,
  // else the managing admin), so the unit reads as "teacher"
  teacher: { one: "teacher", many: "teachers", One: "Teacher", Many: "Teachers" },
  grade: { one: "grade", many: "grades", One: "Grade", Many: "Grades" },
  // engagement segments learners by dosage tier (avg lessons/week); the unit
  // is the tier itself
  engagement: {
    one: "engagement tier",
    many: "engagement tiers",
    One: "Engagement tier",
    Many: "Engagement tiers",
  },
  // Only reachable under the all-organizations scope, where districts are the
  // peer set schools are inside one. Not in DIMENSIONS below: it is not a
  // breakdown an admin picks, it is what the cross-district scope splits by.
  organization: {
    one: "district",
    many: "districts",
    One: "District",
    Many: "Districts",
  },
};
const DIMENSIONS = [
  ["school", "School"],
  ["teacher", "Teacher"],
  ["grade", "Grade"],
  ["engagement", "Engagement tier"],
];

// Engagement tiers are *ordinal* (low dose -> high dose), so the dosage bands
// get a sequential single-hue ramp rather than the categorical SERIES_HUES:
// more dosage reads as more-saturated green. Non-dose buckets take neutrals --
// churned the same gray as robot "dark", out-of-session a lighter gray (a
// school break, not a dropout). Colors switch on the manifest's `kind` (not
// hardcoded tier keys), so adding or renaming a band in engagement/config.py
// needs no edit here -- only keep TIER_RAMP at least as long as the band count
// (ColorBrewer Greens; the palest step is dropped so the low bands, where most
// learners sit, don't wash out to near-white).
const TIER_RAMP = [
  "#c7e9c0",
  "#a1d99b",
  "#74c476",
  "#41ab5d",
  "#238b45",
  "#005a32",
];
const TIER_CHURNED_COLOR = "#8a8983";
// Out-of-session (summer/break) neutral -- a lighter gray than "dark"/"churned"
// so a parked-for-the-summer state reads as recessive, not as a problem. Shared
// with the robot-utilization fleet strip so both dashboards color it the same.
const OUT_OF_SESSION_COLOR = "#dedcd3";
const TIER_KIND_COLOR = { churned: TIER_CHURNED_COLOR, out_of_session: OUT_OF_SESSION_COLOR };
const tierColor = (tier) =>
  tier.kind === "band"
    ? TIER_RAMP[tier.order] || TIER_RAMP[TIER_RAMP.length - 1]
    : TIER_KIND_COLOR[tier.kind] || TIER_CHURNED_COLOR;

// Mastery-composition palette (green / blue / amber for outright / eventually /
// not-yet). CVD- and contrast-checked in light mode via the dataviz palette
// validation. Lives here because the curriculum table and the Overview's gap
// spark are the same part-to-whole encoding at two sizes — one definition, or
// they drift. MASTERY_NOT_INK is the accessible amber for *text* on white; the
// raw amber is a fill color only.
const MASTERY_OUTRIGHT = "#008300";
const MASTERY_EVENTUALLY = "#2a78d6";
const MASTERY_NOT = "#eda100";
const MASTERY_NOT_INK = "#a06b00";

// The three bands as one ordered list, because their ORDER is part of the
// encoding: a composition bar reads left to right as most independent to least,
// so outright must come first and not-yet last wherever the split is drawn.
//
// Four consumers need it -- the curriculum table, the Overview's spark, the
// district rollup and the schools matrix tooltip. Each spelling its own shape is
// individually fine and collectively a standing invitation to add a fourth band
// in three places.
//
// `label` names the band, `note` says how a learner got there (shown in legends
// where the distinction is the whole point), and `short` is the compact wording
// for a tooltip listing all three on one line.
const MASTERY_BANDS = [
  {
    key: "mastered_outright",
    color: MASTERY_OUTRIGHT,
    label: "Mastered outright",
    note: "first try, no prior attempts",
    short: "outright",
  },
  {
    key: "eventually_mastered",
    color: MASTERY_EVENTUALLY,
    label: "Eventually mastered",
    note: "after ABii re-teaching",
    short: "ABii-assisted",
  },
  {
    key: "not_mastered",
    color: MASTERY_NOT,
    label: "Not yet mastered",
    note: "",
    short: "not yet",
  },
];

// Robot activity-state palette, validated via the dataviz skill: green↔amber CVD
// separation passes (protan ΔE 18.4 — the default #0ca30c/#e0920a status pair
// fails at 1.7, so Active gets its own darker green here). The gray is a
// deliberate neutral: "dark" is the *absence* of signal, not an identity hue.
// Amber sits below 3:1 contrast on white, so state is never color-alone —
// legend, tooltips, and the worklist table all carry the labels.
//
// Lives here for the same reason as the mastery palette above: the Fleet tab's
// stacked bars and the Overview's fleet spark are one encoding at two sizes.
// RobotsTab re-exports these so its existing importers don't all have to move.
const UTIL_STATES = {
  active: { label: "Active", color: "#0a6e0a" },
  connected: { label: "Connected, no lessons", color: "#e0920a" },
  dark: { label: "Dark (not connecting)", color: "#8a8983" },
};
const STACK_ORDER = ["active", "connected", "dark"];

// ---- verdict tones ----------------------------------------------------------
// What a card or a pill is ASKING OF THE READER. This is a different axis from
// every palette above it: those encode what a thing IS (a mastery band, a robot
// state, a quadrant), this encodes what you are expected to do about it. Four
// levels, and the top one is rationed:
//
//   action     something must be DONE, and there is a deadline or a broken thing
//              behind it — a lapsed subscription, a renewal inside its window.
//              Red, and red is reserved for exactly this.
//   attention  unusual enough that someone should look. A strong recommendation,
//              not an obligation.
//   advisory   a recommendation — worth a look when there is time.
//   clear      nothing to do.
//
// Red is reserved for `action` alone. Let it carry "attention" too and a
// mostly-analytical dashboard shows three red "Action needed" chips on its
// landing page with no way left to mark the one item that actually has a
// deadline. Most of what this dashboard produces is a recommendation; almost none
// of it is an order.
//
// Advisory takes BLUE rather than a paler amber so the two recommendation levels
// separate by hue instead of by saturation — a saturation-only pair fails the
// same CVD test the robot palette documents above.
//
// Three colors per tone, because one is not enough at chip size: `color` is the
// dot/fill, `ink` the text (amber at 2.2:1 on white cannot BE the text — that is
// what MASTERY_NOT_INK exists for), `tint` the chip's own surface.
const ADVISORY = "#2a6db8";
const ADVISORY_INK = "#22568f";
const TONES = {
  action: {
    key: "action",
    rank: 4,
    label: "Action needed",
    color: STATUS.critical.color,
    ink: "#b32d2d",
    tint: "rgba(208, 59, 59, 0.10)",
  },
  attention: {
    key: "attention",
    rank: 3,
    label: "Needs a look",
    color: STATUS.warning.color,
    ink: MASTERY_NOT_INK,
    tint: "rgba(224, 146, 10, 0.12)",
  },
  advisory: {
    key: "advisory",
    rank: 2,
    label: "Recommended",
    color: ADVISORY,
    ink: ADVISORY_INK,
    tint: "rgba(42, 109, 184, 0.10)",
  },
  clear: {
    key: "clear",
    rank: 1,
    label: "All clear",
    color: UTIL_STATES.active.color,
    ink: UTIL_STATES.active.color,
    tint: "rgba(10, 110, 10, 0.09)",
  },
};

// The loudest of several tones — one card that folds two concerns (the fleet's
// robots and its subscriptions) still wears one chip.
const worstTone = (...keys) =>
  keys
    .filter((k) => TONES[k])
    .sort((a, b) => TONES[b].rank - TONES[a].rank)[0] || null;

// Quadrant class -> tone, for rows the status cascade left unclaimed. A quadrant
// is a POSITION (below the district median on both axes), never an obligation, so
// nothing here maps to `action`: the worst a placement can earn is a look.
const QUADRANT_TONE = { good: "clear", warning: "advisory", critical: "attention" };

// Attention is a MEASURE, not a state, and it is the only continuous series
// either dashboard plots against a mastery encoding -- the session trace behind a
// Classroom Matrix cell draws it beside per-question performance. So it needs a
// hue that is not on the mastery ramp and not one of the two absence neutrals,
// or "how much did they look at the robot" would read as a mastery band.
//
// Purple, and specifically this purple, because the pair it has to survive is
// (attention, mastered): validated against MASTERY_OUTRIGHT via the dataviz
// validator at ΔE 27.9 deutan / 13.1 tritan / 34.1 normal, all pass. The obvious
// alternative -- SERIES_HUES[0] blue -- passes deutan and protan but drops to
// 7.6 on tritan against the same green, which is the floor. It is the same value
// as SUBJECT_COLORS.READING and deliberately named separately: a subject and a
// measure that happen to share a hue must be free to stop sharing it.
const ATTENTION = "#4a3aa7";

// Subject is a categorical split (not ordinal), so it takes two fixed
// categorical hues from SERIES_HUES rather than the sequential tier ramp. Used
// to color the per-subject dose-response series; legend/x-axis carry the
// meaning, so blue vs. purple only needs to be distinguishable, not semantic.
const SUBJECT_COLORS = { MATH: "#2a78d6", READING: "#4a3aa7" };
const SUBJECT_LABELS = { MATH: "Math", READING: "Reading" };

// Where a unit sits relative to the grade of the learner who worked it, for the
// curriculum lens's row chips.
//
// NO HUE, deliberately -- this is the one encoding on the page that could not
// have one. A row already shows a subject hue (blue/purple) beside a mastery bar
// (green/blue/amber), so a below/above pair would be the sixth and seventh
// categorical hues on one line. Searching OKLCH exhaustively for a two-pole pair
// that clears the dataviz gates against those four found 86,650 candidates and a
// best-case tritan of only ΔE 7.6 -- inside the 6-8 band that is legal ONLY with
// secondary encoding, and the winners were a maroon/sage pair that reads as a
// washed-out MASTERY_OUTRIGHT. The honest conclusion is that the palette is
// full: direction is carried by a glyph and a word, which is what the gates
// would have demanded as the backstop anyway, and off-grade simply gets the
// darker of two neutrals so it is the thing that catches a scanning eye.
//
// `on` is intentionally the quiet one. Working at grade level is the
// unremarkable case and shouldn't spend ink.
const LEVEL_REL = {
  below: {
    key: "below",
    glyph: "\u2193",
    label: "below grade",
    long: "below grade level",
    tint: "rgba(43, 42, 40, 0.07)",
    ink: PROSE,
  },
  on: {
    key: "on",
    glyph: "",
    label: "on grade",
    long: "on grade level",
    tint: "transparent",
    ink: MUTED,
  },
  above: {
    key: "above",
    glyph: "\u2191",
    label: "above grade",
    long: "above grade level",
    tint: "rgba(43, 42, 40, 0.07)",
    ink: PROSE,
  },
  unknown: {
    key: "unknown",
    glyph: "",
    label: "level unknown",
    long: "content level unknown",
    tint: "transparent",
    ink: MUTED,
  },
};

// Display order wherever the split is listed (legend, tooltip, filter): signed,
// low to high, so it reads like a number line rather than an arbitrary set.
const LEVEL_REL_ORDER = ["below", "on", "above"];

// How much of a row's work sat off grade, as a surface weight. This is the one
// part of the encoding that gets a fill, and it is legitimate because the
// variable here is a MAGNITUDE (the off-grade share), not the direction -- so it
// takes a sequential ramp, light to dark, in no hue at all. Direction stays in
// the chip's words; weight is what makes an off-grade row catch a scanning eye.
//
// It matters that this is share-driven rather than category-driven: on a real
// all-grades district roster almost every unit is worked from several grades
// (82 of 90 for our highest-volume district), so a purely categorical tint
// would never fire on the default view. Steps are coarse on purpose -- a chip
// is too small to read five levels of gray, and the exact figure is in the text
// beside it.
const LEVEL_OFF_STEPS = [
  { min: 0.5, tint: "rgba(43, 42, 40, 0.11)", ink: INK },
  { min: 0.2, tint: "rgba(43, 42, 40, 0.06)", ink: PROSE },
  { min: 0, tint: "transparent", ink: MUTED },
];

export {
  STATUS,
  SERIES_HUES,
  INK,
  MUTED,
  GRID,
  CONTEXT,
  SURFACE,
  CARD_RADIUS,
  CARD_BORDER,
  ROW_DIVIDER,
  nestBg,
  TRAY,
  PROSE,
  SEG_NOUN,
  DIMENSIONS,
  TIER_RAMP,
  OUT_OF_SESSION_COLOR,
  tierColor,
  MASTERY_OUTRIGHT,
  MASTERY_EVENTUALLY,
  MASTERY_NOT,
  MASTERY_NOT_INK,
  MASTERY_BANDS,
  UTIL_STATES,
  STACK_ORDER,
  ADVISORY,
  TONES,
  worstTone,
  QUADRANT_TONE,
  ATTENTION,
  SUBJECT_COLORS,
  SUBJECT_LABELS,
  LEVEL_REL,
  LEVEL_REL_ORDER,
  LEVEL_OFF_STEPS,
};
